import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import * as api from '../api/blueprint.api.js'
import * as snapshotApi from '../api/snapshot.api.js'
import { ApiError } from '../api/client.js'
import { useAuth } from '../auth/AuthProvider.tsx'

function pesan(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Tidak dapat menghubungi server'
  const detail = error.details?.map((d) => `${d.field}: ${d.message}`).join('; ')
  return detail !== undefined && detail !== '' ? detail : error.message
}

const VERDICT_CLASS: Record<api.Verdict, string> = {
  CUKUP: 'badge-diterbitkan',
  // Pas bukan kabar baik: terpenuhi tanpa cadangan sama sekali.
  PAS: 'badge-diajukan',
  KURANG: 'badge-ditolak',
}

const KIND_LABEL: Record<api.ViolationKind, string> = {
  SESI_SAMA_HARI: 'Sesi lain hari yang sama',
  KARANTINA_TUK: 'Karantina TUK',
  KARANTINA_NASIONAL: 'Karantina nasional',
}

function labelSesi(s: snapshotApi.SessionRow): string {
  return `${s.tuk.code} · ${s.date.slice(0, 10)} · sesi ke-${s.sessionOrder} — ${s.name}`
}

/**
 * Blueprint, kecukupan station, dan penyusun sirkuit — W2-C01 dan W2-C04.
 *
 * Halaman ini ada karena laporan kecukupan hanya berguna bila dibaca **sebelum**
 * gelombang disusun. Kekurangan station yang baru ketahuan saat menyegel
 * snapshot berarti gelombang ditunda, dan gelombang nasional tidak dapat
 * ditunda sebagian.
 */
export function BlueprintPage(): ReactNode {
  const { user } = useAuth()
  const bolehUbah = user?.roles.some((r) => r.role === 'PANITIA_PUSAT') ?? false

  const [items, setItems] = useState<readonly api.Blueprint[]>([])
  const [kategori, setKategori] = useState<readonly api.Category[]>([])
  const [sesi, setSesi] = useState<readonly snapshotApi.SessionRow[]>([])
  const [error, setError] = useState<string | null>(null)

  const muat = useCallback(async () => {
    try {
      const [b, k, s] = await Promise.all([
        api.list(),
        api.categories(),
        snapshotApi.listSessions(),
      ])
      setItems(b)
      setKategori(k)
      setSesi(s)
      setError(null)
    } catch (err) {
      setError(pesan(err))
    }
  }, [])

  useEffect(() => {
    void muat()
  }, [muat])

  return (
    <div>
      <h1>Blueprint dan Sirkuit</h1>
      <p className="hint">
        Blueprint menentukan berapa station tiap kategori harus ada pada satu sirkuit. Kecukupannya
        dinilai terhadap station yang <strong>lolos aturan paparan</strong> untuk slot sesi yang
        dituju — bukan terhadap isi katalog. Kategori dengan tiga station yang ketiganya baru
        dipakai pekan lalu tidak punya station untuk sesi berikutnya.
      </p>

      {error !== null && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      <Penyusun blueprints={items} sesi={sesi} bolehUbah={bolehUbah} onError={setError} />

      <h2>Blueprint tersimpan</h2>
      {items.length === 0 ? (
        <p className="dt-empty">Belum ada blueprint.</p>
      ) : (
        <div className="kartu-grid">
          {items.map((b) => (
            <section key={b.id} className="kartu">
              <div className="kartu-kepala">
                <span>{b.rotations} rotasi</span>
                <span>{b.createdBy}</span>
              </div>
              <div className="kartu-nama">{b.name}</div>
              {b.notes !== null && <p className="kartu-catatan">{b.notes}</p>}
              <ul className="ringkas-list">
                {b.requirements.map((r) => (
                  <li key={r.category}>
                    <span>{r.category}</span>
                    <span>
                      {r.minStations}
                      {r.minSkdiLevel === null ? '' : ` · ≥ ${r.minSkdiLevel}`}
                    </span>
                  </li>
                ))}
              </ul>
              {bolehUbah && (
                <div className="page-actions">
                  <button
                    onClick={() => {
                      void (async () => {
                        try {
                          await api.remove(b.id)
                          await muat()
                        } catch (err) {
                          setError(pesan(err))
                        }
                      })()
                    }}
                  >
                    Hapus
                  </button>
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      {bolehUbah && <FormBlueprint kategori={kategori} onSimpan={muat} onError={setError} />}
    </div>
  )
}

/** Penilaian kecukupan dan penyusunan sirkuit untuk satu slot sesi. */
function Penyusun({
  blueprints,
  sesi,
  bolehUbah,
  onError,
}: {
  blueprints: readonly api.Blueprint[]
  sesi: readonly snapshotApi.SessionRow[]
  bolehUbah: boolean
  onError: (m: string) => void
}): ReactNode {
  const navigate = useNavigate()
  const [blueprintId, setBlueprintId] = useState('')
  const [sessionId, setSessionId] = useState('')
  const [seed, setSeed] = useState('')
  const [hasil, setHasil] = useState<api.AssessResult | null>(null)
  const [sirkuit, setSirkuit] = useState<api.ComposeResult | null>(null)
  const [busy, setBusy] = useState(false)

  const sesiTerpilih = sesi.find((s) => s.id === sessionId)

  async function nilai(): Promise<void> {
    setBusy(true)
    setSirkuit(null)
    try {
      setHasil(await api.assess(blueprintId, sessionId))
    } catch (err) {
      onError(pesan(err))
    } finally {
      setBusy(false)
    }
  }

  async function susun(): Promise<void> {
    setBusy(true)
    try {
      setSirkuit(await api.compose(blueprintId, sessionId, seed))
    } catch (err) {
      onError(pesan(err))
    } finally {
      setBusy(false)
    }
  }

  async function jadikanSnapshot(kode: readonly string[]): Promise<void> {
    setBusy(true)
    try {
      const dibuat = await snapshotApi.build(sessionId, kode)
      navigate(`/snapshot?buka=${dibuat.id}`)
    } catch (err) {
      onError(pesan(err))
    } finally {
      setBusy(false)
    }
  }

  const siap = blueprintId !== '' && sessionId !== ''

  return (
    <section className="terbit-form">
      <h2>Kecukupan dan komposisi</h2>

      {sesi.length === 0 ? (
        <p className="notice">
          Belum ada sesi terjadwal. Penilaian kecukupan dan penyusunan sirkuit seluruhnya ditentukan
          slot sesi — TUK, tanggal, dan urutan sesi — sehingga keduanya tidak dapat dijalankan tanpa
          sesi. Penjadwalan sesi belum termasuk wave ini.
        </p>
      ) : (
        <>
          <div className="terbit-window">
            <label>
              Blueprint
              <select value={blueprintId} onChange={(e) => setBlueprintId(e.target.value)}>
                <option value="">— pilih —</option>
                {blueprints.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} ({b.rotations} rotasi)
                  </option>
                ))}
              </select>
            </label>

            <label>
              Sesi
              <select value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
                <option value="">— pilih —</option>
                {sesi.map((s) => (
                  <option key={s.id} value={s.id}>
                    {labelSesi(s)}
                    {s.snapshot === null ? '' : ` — snapshot ${s.snapshot.state}`}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Benih (opsional)
              <input
                value={seed}
                onChange={(e) => setSeed(e.target.value)}
                placeholder="otomatis dari slot"
              />
            </label>
          </div>

          <p className="hint">
            Komposisi bersifat <strong>deterministik</strong>: benih yang sama selalu menghasilkan
            urutan yang sama. Bila ada yang mempersoalkan mengapa station tertentu terpilih,
            jawabannya dapat dihitung ulang — bukan &ldquo;hasil pengacakan saat itu&rdquo;.
          </p>

          {sesiTerpilih?.snapshot !== undefined &&
            sesiTerpilih.snapshot !== null &&
            sesiTerpilih.snapshot.state !== 'DIBATALKAN' && (
              <p className="notice">
                Sesi ini sudah punya snapshot berstatus {sesiTerpilih.snapshot.state}. Menyusun
                ulang menuntut snapshot itu dibatalkan lebih dulu.
              </p>
            )}

          <div className="page-actions">
            <button disabled={!siap || busy} onClick={() => void nilai()}>
              Nilai kecukupan
            </button>
            {bolehUbah && (
              <button disabled={!siap || busy} onClick={() => void susun()}>
                Susun sirkuit
              </button>
            )}
          </div>
        </>
      )}

      {hasil !== null && <Kecukupan hasil={hasil} />}
      {sirkuit !== null && (
        <Sirkuit
          hasil={sirkuit}
          bolehUbah={bolehUbah}
          busy={busy}
          onJadikanSnapshot={(kode) => void jadikanSnapshot(kode)}
        />
      )}
    </section>
  )
}

function Kecukupan({ hasil }: { hasil: api.AssessResult }): ReactNode {
  const p = hasil.penilaian

  return (
    <>
      <div className="version-head">
        <h3>Kecukupan station</h3>
        <span className={`badge ${p.feasible ? 'badge-diterbitkan' : 'badge-ditolak'}`}>
          {p.feasible ? 'DAPAT DISUSUN' : 'TIDAK DAPAT DISUSUN'}
        </span>
      </div>

      <div className="ringkas-kartu">
        <div>
          <strong>{p.rotations}</strong>
          <span>rotasi sirkuit</span>
        </div>
        <div>
          <strong>{p.totalRequired}</strong>
          <span>diminta blueprint</span>
        </div>
        <div>
          <strong>{p.totalAvailable}</strong>
          <span>tersedia setelah paparan</span>
        </div>
        <div>
          <strong>{hasil.tertahanPaparan.length}</strong>
          <span>tertahan aturan paparan</span>
        </div>
      </div>

      <div className="dt">
        <table>
          <thead>
            <tr>
              <th>Kategori</th>
              <th>Butuh</th>
              <th>Di katalog</th>
              <th>Dapat dipakai</th>
              <th>Keterangan</th>
            </tr>
          </thead>
          <tbody>
            {p.categories.map((c) => (
              <tr key={c.category}>
                <td>{c.category}</td>
                <td>{c.required}</td>
                <td>{c.inCatalog}</td>
                <td>
                  <span className={`badge ${VERDICT_CLASS[c.verdict]}`}>
                    {c.available} · {c.verdict}
                  </span>
                </td>
                <td className="hint">{c.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {p.problems.length > 0 && (
        <ul className="ringkas-list">
          {p.problems.map((m) => (
            <li key={m}>
              <span className="error">{m}</span>
            </li>
          ))}
        </ul>
      )}

      {hasil.tertahanPaparan.length > 0 && (
        <details>
          <summary>{hasil.tertahanPaparan.length} station tertahan aturan paparan</summary>
          <p className="hint">
            Ujian nasional serentak memang memakai station yang sama di seluruh TUK — itu
            rancangannya. Yang ditahan di sini adalah pemakaian pada slot yang{' '}
            <strong>tidak serentak</strong>.
          </p>
          <ul className="ringkas-list">
            {hasil.tertahanPaparan.map((v) => (
              <li key={v.stationSourceCode}>
                <span>
                  <code>{v.stationSourceCode}</code>
                  <br />
                  <span className="hint">{v.message}</span>
                </span>
                <span className="badge badge-ditolak">{KIND_LABEL[v.kind]}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  )
}

function Sirkuit({
  hasil,
  bolehUbah,
  busy,
  onJadikanSnapshot,
}: {
  hasil: api.ComposeResult
  bolehUbah: boolean
  busy: boolean
  onJadikanSnapshot: (kode: readonly string[]) => void
}): ReactNode {
  if (!hasil.berhasil) {
    return (
      <>
        <h3>Sirkuit</h3>
        <p role="alert" className="error">
          Sirkuit tidak dapat disusun.
        </p>
        <ul className="ringkas-list">
          {hasil.masalah.map((m) => (
            <li key={m}>
              <span>{m}</span>
            </li>
          ))}
        </ul>
      </>
    )
  }

  return (
    <>
      <div className="version-head">
        <h3>Sirkuit — {hasil.sirkuit.length} rotasi</h3>
        <span className="hint">
          benih <code>{hasil.seed}</code>
        </span>
      </div>

      <div className="dt">
        <table>
          <thead>
            <tr>
              <th>Rotasi</th>
              <th>Station</th>
              <th>Kategori</th>
            </tr>
          </thead>
          <tbody>
            {hasil.sirkuit.map((s) => (
              <tr key={s.position}>
                <td>{s.position}</td>
                <td>
                  <code>{s.stationSourceCode}</code>
                </td>
                <td>{s.category}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {bolehUbah && (
        <div className="page-actions">
          <button
            disabled={busy}
            onClick={() => onJadikanSnapshot(hasil.sirkuit.map((s) => s.stationSourceCode))}
          >
            Susun snapshot dari sirkuit ini
          </button>
          <span className="hint">
            Snapshot memuat salinan penuh isi station, bukan rujukan. Belum tersegel — isinya masih
            dapat disusun ulang sampai ada panitia lain yang menyegelnya.
          </span>
        </div>
      )}
    </>
  )
}

interface BarisKebutuhan {
  readonly category: string
  readonly minStations: number
  readonly minSkdiLevel: string
}

function FormBlueprint({
  kategori,
  onSimpan,
  onError,
}: {
  kategori: readonly api.Category[]
  onSimpan: () => Promise<void>
  onError: (m: string) => void
}): ReactNode {
  const [nama, setNama] = useState('')
  const [rotations, setRotations] = useState(12)
  const [notes, setNotes] = useState('')
  const [baris, setBaris] = useState<readonly BarisKebutuhan[]>([])
  const [busy, setBusy] = useState(false)

  const totalDiminta = baris.reduce((n, b) => n + b.minStations, 0)

  function ubah(category: string, patch: Partial<BarisKebutuhan>): void {
    setBaris((prev) => prev.map((b) => (b.category === category ? { ...b, ...patch } : b)))
  }

  function alih(category: string, aktif: boolean): void {
    setBaris((prev) =>
      aktif
        ? [...prev, { category, minStations: 1, minSkdiLevel: '' }]
        : prev.filter((b) => b.category !== category),
    )
  }

  async function simpan(): Promise<void> {
    setBusy(true)
    try {
      await api.create({
        name: nama.trim(),
        rotations,
        notes: notes.trim() === '' ? null : notes.trim(),
        requirements: baris.map((b) => ({
          category: b.category,
          minStations: b.minStations,
          ...(b.minSkdiLevel === '' ? {} : { minSkdiLevel: b.minSkdiLevel }),
        })),
      })
      setNama('')
      setNotes('')
      setBaris([])
      await onSimpan()
    } catch (err) {
      onError(pesan(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="terbit-form">
      <h2>Blueprint baru</h2>

      <div className="terbit-window">
        <label>
          Nama
          <input value={nama} onChange={(e) => setNama(e.target.value)} />
        </label>
        <label>
          Rotasi
          <select value={rotations} onChange={(e) => setRotations(Number(e.target.value))}>
            <option value={12}>12</option>
            <option value={14}>14</option>
          </select>
        </label>
        <label>
          Catatan
          <input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </div>

      <p className="hint">
        Kategori diambil dari katalog, bukan diketik bebas: blueprint yang menyebut kategori yang
        tidak ada di katalog tidak akan pernah dapat dipenuhi. Angka dalam kurung adalah jumlah
        station kategori itu di katalog — sebelum aturan paparan.
      </p>

      {kategori.length === 0 ? (
        <p className="dt-empty">Katalog masih kosong; tarik station dari Bank Soal lebih dulu.</p>
      ) : (
        <ul className="pilih-penguji">
          {kategori.map((k) => {
            const dipilih = baris.find((b) => b.category === k.category)
            return (
              <li key={k.category}>
                <label>
                  <input
                    type="checkbox"
                    checked={dipilih !== undefined}
                    onChange={(e) => alih(k.category, e.target.checked)}
                  />
                  <span>
                    {k.category} <span className="hint">({k.stations})</span>
                  </span>
                </label>
                {dipilih !== undefined && (
                  <span className="terbit-window">
                    <label>
                      Minimal station
                      <input
                        type="number"
                        min={1}
                        max={k.stations}
                        value={dipilih.minStations}
                        onChange={(e) =>
                          ubah(k.category, { minStations: Number(e.target.value) || 1 })
                        }
                      />
                    </label>
                    <label>
                      Level SKDI minimum
                      <select
                        value={dipilih.minSkdiLevel}
                        onChange={(e) => ubah(k.category, { minSkdiLevel: e.target.value })}
                      >
                        <option value="">tanpa batas</option>
                        {api.SKDI_LEVELS.map((l) => (
                          <option key={l} value={l}>
                            {l}
                          </option>
                        ))}
                      </select>
                    </label>
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {totalDiminta > rotations && (
        <p className="peringatan">
          Kebutuhan berjumlah {totalDiminta} station sedangkan sirkuit hanya {rotations} rotasi.
        </p>
      )}

      <div className="page-actions">
        <button
          disabled={busy || nama.trim() === '' || baris.length === 0 || totalDiminta > rotations}
          onClick={() => void simpan()}
        >
          Simpan blueprint
        </button>
        <span className="hint">
          {baris.length} kategori · {totalDiminta} dari {rotations} rotasi terikat kebutuhan
        </span>
      </div>
    </section>
  )
}
